import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainLayouts from "../../layout/MainLayouts";
import BlogCard from "./BlogCard";
import { fetchCategories, fetchPosts } from "../../api/blogClient";
import { formatBlogDate } from "./blogUtils";

const PER_PAGE = 9;

function FeaturedPost({ post }) {
  const { t, i18n } = useTranslation();
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group grid grid-cols-1 lg:grid-cols-2 bg-white rounded-[32px] overflow-hidden border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.10)] transition-shadow duration-500 mb-16"
    >
      <div className="relative h-[260px] lg:h-full min-h-[320px] overflow-hidden bg-gray-100">
        {post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        )}
        <span className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-primary text-white text-[10px] font-bold uppercase tracking-wider shadow-lg">
          {t("blogPage.featured")}
        </span>
      </div>
      <div className="p-8 md:p-12 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-5 text-[11px] font-bold uppercase tracking-wider text-gray-400">
          {post.categories?.[0] && <span className="text-primary">{post.categories[0].name}</span>}
          <span>{formatBlogDate(post.published_at, i18n.language)}</span>
          {post.reading_time_minutes && <span>{t("blogPage.minRead", { count: Number(post.reading_time_minutes) })}</span>}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#0b0f19] leading-tight mb-5 group-hover:text-primary transition-colors">
          {post.title}
        </h2>
        {post.excerpt && <p className="text-gray-500 text-lg leading-relaxed mb-8 line-clamp-4">{post.excerpt}</p>}
        <span className="inline-flex items-center gap-2 font-bold text-[#0b0f19] group-hover:text-primary transition-colors">
          {t("blogPage.readArticle")}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 transition-transform group-hover:translate-x-1">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const tag = searchParams.get("tag") || "";
  const search = searchParams.get("search") || "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const [searchInput, setSearchInput] = useState(search);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [posts, setPosts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const isFiltered = !!(category || tag || search);
  const showFeatured = featured && !isFiltered && page === 1;

  // Filters live in the URL so filtered views and pages can be shared and survive refresh.
  const updateParams = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
    if (!("page" in changes)) next.delete("page");
    setSearchParams(next);
  };

  useEffect(() => {
    document.title = `${t("blogPage.title")} | E1X Academy`;
    fetchCategories().then(setCategories).catch(() => {});
    fetchPosts({ featured: 1, per_page: 1 }).then(({ posts: list }) => setFeatured(list[0] || null)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => setSearchInput(search), [search]);

  // Debounced search box -> URL.
  useEffect(() => {
    if (searchInput === search) return undefined;
    const id = setTimeout(() => updateParams({ search: searchInput.trim() }), 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchPosts({ category, tag, search, page, per_page: PER_PAGE })
      .then((res) => {
        if (cancelled) return;
        setPosts(res.posts);
        setMeta(res.meta);
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => { cancelled = true; };
  }, [category, tag, search, page]);

  // Don't repeat the featured post in the grid right below it.
  const gridPosts = showFeatured ? posts.filter((p) => p.id !== featured.id) : posts;
  const lastPage = meta?.last_page || 1;
  const activeTagName = tag && posts.flatMap((p) => p.tags || []).find((x) => x.slug === tag)?.name;

  return (
    <MainLayouts>
      {/* Hero */}
      <div className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 bg-[#0b0f19] overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-primary/20 blur-[120px]" />
        <div className="relative z-10 max-w-[1560px] mx-auto px-6">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary mb-6 flex items-center gap-3">
            <span className="block w-8 h-[2px] bg-primary" />
            {t("blogPage.kicker")}
          </div>
          <h1 className="text-white text-5xl md:text-7xl lg:text-[90px] font-black mb-6 leading-[1.05] tracking-tight">
            {t("blogPage.title")}<span className="text-primary">.</span>
          </h1>
          <p className="text-gray-400 text-xl md:text-2xl font-light max-w-2xl leading-relaxed">{t("blogPage.subtitle")}</p>

          <div className="relative mt-10 max-w-xl">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("blogPage.searchPlaceholder")}
              className="w-full pl-14 pr-5 py-4 rounded-full bg-white/10 border border-white/15 text-white placeholder-gray-400 focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      </div>

      <div className="max-w-[1560px] mx-auto px-6 py-16 lg:py-24">
        {/* Category chips */}
        {categories.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-2 mb-12 -mx-6 px-6 md:mx-0 md:px-0 md:flex-wrap">
            {[{ slug: "", name: t("blogPage.allTopics") }, ...categories].map((c) => (
              <button
                key={c.slug || "all"}
                onClick={() => updateParams({ category: c.slug, tag: "" })}
                className={`shrink-0 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                  category === c.slug ? "bg-primary text-white" : "bg-gray-100 text-[#0b0f19] hover:bg-gray-200"
                }`}
              >
                {c.name}
                {c.post_count && Number(c.post_count) > 0 && <span className="ml-2 opacity-60">{c.post_count}</span>}
              </button>
            ))}
          </div>
        )}

        {tag && (
          <div className="mb-10 flex items-center gap-3 text-sm">
            <span className="text-gray-500">{t("blogPage.taggedWith")}</span>
            <span className="px-3 py-1.5 rounded-full bg-[#0b0f19] text-white font-bold">#{activeTagName || tag}</span>
            <button onClick={() => updateParams({ tag: "" })} className="text-primary font-bold hover:underline">{t("blogPage.clear")}</button>
          </div>
        )}

        {showFeatured && <FeaturedPost post={featured} />}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="bg-white rounded-3xl overflow-hidden border border-gray-100 animate-pulse">
                <div className="h-[220px] bg-gray-200" />
                <div className="p-8 space-y-3">
                  <div className="h-3 w-24 bg-gray-200 rounded" />
                  <div className="h-6 w-3/4 bg-gray-200 rounded" />
                  <div className="h-3 w-full bg-gray-200 rounded" />
                  <div className="h-3 w-2/3 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="py-24 text-center text-gray-500">{t("blogPage.loadError")}</div>
        ) : gridPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {gridPosts.map((post) => <BlogCard key={post.id} post={post} />)}
          </div>
        ) : showFeatured ? null : (
          <div className="py-28 px-6 flex flex-col items-center text-center bg-white rounded-[32px] border border-gray-100 shadow-sm">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-6">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-[#0b0f19] mb-2">
              {isFiltered ? t("blogPage.noMatchTitle") : t("blogPage.emptyTitle")}
            </h3>
            <p className="text-gray-500 max-w-md">{isFiltered ? t("blogPage.noMatchText") : t("blogPage.emptyText")}</p>
            {isFiltered ? (
              <button onClick={() => setSearchParams(new URLSearchParams())} className="mt-8 px-6 py-3 rounded-full bg-gray-100 hover:bg-gray-200 text-[#0b0f19] font-bold text-sm transition-colors">
                {t("blogPage.clearFilters")}
              </button>
            ) : (
              <Link to="/courses" className="mt-8 px-6 py-3 rounded-full bg-primary hover:bg-[#b04233] text-white font-bold text-sm transition-colors">
                {t("blogPage.exploreCourses")}
              </Link>
            )}
          </div>
        )}

        {/* Pagination */}
        {!loading && lastPage > 1 && (
          <div className="mt-16 flex items-center justify-center gap-2">
            <button
              onClick={() => updateParams({ page: String(page - 1) })}
              disabled={page <= 1}
              className="px-5 py-2.5 rounded-full border border-gray-200 text-sm font-bold text-[#0b0f19] hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              {t("blogPage.previous")}
            </button>
            {Array.from({ length: lastPage }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === lastPage || Math.abs(n - page) <= 1)
              .map((n, i, arr) => (
                <React.Fragment key={n}>
                  {i > 0 && n - arr[i - 1] > 1 && <span className="px-1 text-gray-400">…</span>}
                  <button
                    onClick={() => updateParams({ page: String(n) })}
                    className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${n === page ? "bg-primary text-white" : "text-[#0b0f19] hover:bg-gray-100"}`}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
            <button
              onClick={() => updateParams({ page: String(page + 1) })}
              disabled={page >= lastPage}
              className="px-5 py-2.5 rounded-full border border-gray-200 text-sm font-bold text-[#0b0f19] hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              {t("blogPage.next")}
            </button>
          </div>
        )}
      </div>
    </MainLayouts>
  );
}
